import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { seoMissingPages, seoMissingPagesAction } from "@/lib/seo/seo-os.functions";
import { SeoPage, Stat, Panel, Pill, Loading, Empty, DataTable, num } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/missing-pages")({
  head: () => ({ meta: [{ title: "Missing pages · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: MissingPagesPage,
});

const KIND: Record<string, string> = {
  product_state: "Product × state page",
  product_city: "City page",
  tool: "Tool page",
  demand: "Search demand with no page",
};

function MissingPagesPage() {
  const load = useServerFn(seoMissingPages);
  const act = useServerFn(seoMissingPagesAction);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["seo-missing"], queryFn: () => load() });
  const d = q.data as any;
  const open = (d?.open ?? []) as any[];
  const planned = (d?.planned ?? []) as any[];

  const detect = useMutation({
    mutationFn: () =>
      act({ data: { action: "detect", baseUrl: typeof window !== "undefined" ? window.location.origin : undefined } }),
    onSuccess: (r: any) => {
      toast.success(`Checked ${r.checkedAgainst} live pages — ${r.detected} gaps found.`);
      qc.invalidateQueries({ queryKey: ["seo-missing"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Detection failed."),
  });

  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: "planned" | "dismissed" }) => act({ data: { action: "status", ...v } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["seo-missing"] }),
    onError: (e: any) => toast.error(e?.message ?? "Could not update."),
  });

  return (
    <SeoPage
      title="Missing pages"
      description="Pages your site is designed to have but doesn't, plus searches where people already see you and no page targets them. Suggestions only — nothing is created automatically."
      actions={
        <Button onClick={() => detect.mutate()} disabled={detect.isPending}>
          {detect.isPending ? "Checking…" : "Check for gaps"}
        </Button>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Open gaps" value={num(open.length)} />
            <Stat label="Planned" value={num(planned.length)} />
            <Stat label="High priority" value={num(open.filter((o) => o.priority <= 1).length)} />
          </div>
          <Panel title="Open gaps">
            {open.length === 0 ? (
              <Empty>No gaps found. Run a check after your next site scan.</Empty>
            ) : (
              <DataTable
                head={["Suggested page", "Type", "Why", "Priority", ""]}
                rows={open.slice(0, 200).map((m) => [
                  <div key={m.id}>
                    <p className="font-semibold">{m.label}</p>
                    <p className="text-xs text-muted-foreground">{m.path}</p>
                  </div>,
                  KIND[m.kind] ?? m.kind,
                  m.rationale,
                  <Pill key={`${m.id}p`} tone={m.priority <= 1 ? "bad" : m.priority === 2 ? "warn" : "neutral"}>
                    P{m.priority}
                  </Pill>,
                  <div key={`${m.id}a`} className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setStatus.mutate({ id: m.id, status: "planned" })}>
                      Plan
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setStatus.mutate({ id: m.id, status: "dismissed" })}>
                      Dismiss
                    </Button>
                  </div>,
                ])}
              />
            )}
          </Panel>
        </>
      )}
    </SeoPage>
  );
}
